import type { Perimeter } from '@atlaskit/atlassian-context/types';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import {
	getAnalyticsClient,
	isSSR,
	Logger,
	sendPackageOperationalEvent,
} from '../../../common/utils';
import { getBscGlobalState, initializeGlobalState } from '../../../common/utils/bsc-global-state';
import type { PackageLogLevel } from '../../../common/utils/logger/log-level';
import { WebStorageType } from '../../../services/web-storage-service';
import type { BscInitializationResult, CommonConsentBannerProps } from '../../../types';
import { cleanupStorage } from '../cleanup-storage';
import {
	createDocumentCookiePassthrough,
	createDocumentCookiePassthroughSync,
} from './create-document-cookie-passthrough';
import {
	createIndexedDBPassthrough,
	createIndexedDBPassthroughSync,
} from './create-indexed-db-passthrough';
import {
	createWebStoragePassthrough,
	createWebStoragePassthroughSync,
} from './create-web-storage-passthrough';

export type InitializeControlsArgs = CommonConsentBannerProps & {
	product?: string;
	flagEnabled?: boolean;
	webStorageEnabled?: boolean;
	indexedDbEnabled?: boolean;
	/**
	 * Only takes effect when `webStorageEnabled` is `true`.
	 *
	 * When `true`, the web-storage passthrough runs in analytics-only mode for both
	 * `window.localStorage` and `window.sessionStorage`: analytics events for `setItem`
	 * are still emitted, but no consent checks block writes and the behaviour of
	 * `AtlBrowserStorage` is unchanged.
	 *
	 * Use this for telemetry-first rollouts where you want to measure usage of the
	 * controls pipeline without enforcing consent yet. This is the web-storage analogue
	 * of `disableIndexedDbConsentChecks`.
	 *
	 * Defaults to `false`.
	 * @default false
	 */
	disableWebStorageConsentChecks?: boolean;
	/**
	 * The perimeter this instance is running in.
	 *
	 * @deprecated Perimeter is now auto-detected from the Atl-Ctx-Perimeter cookie via
	 * `@atlaskit/atlassian-context`. You no longer need to pass this prop — BSC will
	 * detect the correct perimeter automatically. This prop will be removed in a future release.
	 * See {@link https://hello.atlassian.net/browse/COOK-415 COOK-415}.
	 */
	perimeter?: Perimeter;

	/**
	 * **Scope: cookies AND web storage** (despite the name).
	 *
	 * If set to true, both cookies (via the `document.cookie` setter override) AND
	 * web storage items (via the `localStorage` / `sessionStorage` `setItem`
	 * overrides) that are not categorized will be allowed to pass through.
	 *
	 * Note: this flag is intentionally broad — it controls both surfaces. If you
	 * need to allow uncategorized writes for web storage **only** (without
	 * affecting cookies), use `allowUncategorizedItemsInWebStorageOverrides`
	 * instead.
	 *
	 * @default false
	 */
	allowUncategorizedCookiesInOverrides?: boolean;
	/**
	 * **Scope: web storage ONLY.**
	 *
	 * Web-storage-only counterpart to `allowUncategorizedCookiesInOverrides`.
	 *
	 * If set to true, web storage items (`localStorage` / `sessionStorage`) that
	 * are not categorized will be allowed to be set through overridden `setItem`
	 * calls.
	 *
	 * Unlike `allowUncategorizedCookiesInOverrides` — which acts on **both**
	 * cookies and web storage — this flag has **no effect on cookies**. Use this
	 * when you need to allow uncategorized writes for web storage without
	 * changing cookie behaviour.
	 *
	 * The two flags are independent and additive: web storage allows
	 * uncategorized writes when EITHER flag is `true`. Cookies are unaffected by
	 * this flag in all cases.
	 *
	 * Only takes effect when `webStorageEnabled` is `true`.
	 *
	 * @default false
	 */
	allowUncategorizedItemsInWebStorageOverrides?: boolean;
	/**
	 * **Scope: IndexedDB ONLY.**
	 *
	 * IDB-only counterpart to `allowUncategorizedItemsInWebStorageOverrides`.
	 *
	 * If set to `true`, IndexedDB databases that are not categorized will be
	 * allowed to open through the overridden `indexedDB.open()` call instead of
	 * being blocked. Has **no effect on cookies or web storage**.
	 *
	 * Only takes effect when `indexedDbEnabled` is `true`.
	 *
	 * @default false
	 */
	allowUncategorizedItemsInIndexedDBOverrides?: boolean;
	userId?: string;
	cloudId?: string;
	/**
	 * If set to true, consent checks will be disabled for IndexedDB operations (both the
	 * window.indexedDB override and AtlIndexedDb). Analytics events will still be sent so
	 * that all IDB databases can be tracked. This is intended for rollout testing only and
	 * should not be enabled in production except via feature flag.
	 * @default false
	 */
	disableIndexedDbConsentChecks?: boolean;
	/**
	 * Console log verbosity. Each level includes the levels below it
	 * (e.g. `'warn'` emits errors and warnings). Defaults to `'error'`.
	 * Overridden by the `atl-bsc-debug-mode` cookie (when the
	 * `platform_moonjelly_bsc_debug_mode` FF is enabled), which forces `'debug'`.
	 */
	packageLogLevel?: PackageLogLevel;
};

/**
 * Shared by both `platform_bsc_sync_initialize_controls` branches so the consent-check decision can
 * never diverge between the sync and the legacy path.
 */
const getIndexedDbPassthroughConfig = (): { performConsentChecks: boolean } => {
	const disableIndexedDbConsentChecks = getBscGlobalState().disableIndexedDbConsentChecks ?? false;
	return { performConsentChecks: !disableIndexedDbConsentChecks };
};

export const initializeControls = async (
	args: InitializeControlsArgs = {
		analyticsEnabled: true,
	},
): Promise<BscInitializationResult | undefined> => {
	if (fg('platform_moonjelly_bsc_master_killswitch')) {
		return undefined;
	}

	let initResult: BscInitializationResult | undefined;

	try {
		Logger.debug('initializeControls invoked');
		initializeGlobalState({ ...args, overrideOnceParams: true });

		if (isSSR()) {
			return undefined;
		}

		sendPackageOperationalEvent({
			action: 'bscInitControlsParameters',
			attributes: {
				product: args.product,
				analyticsEnabled: args.analyticsEnabled,
				flagEnabled: args.flagEnabled,
				webStorageEnabled: args.webStorageEnabled,
				indexedDbEnabled: args.indexedDbEnabled,
				disableWebStorageConsentChecks: args.disableWebStorageConsentChecks,
				disableIndexedDbConsentChecks: args.disableIndexedDbConsentChecks,
				allowUncategorizedCookiesInOverrides: args.allowUncategorizedCookiesInOverrides,
				allowUncategorizedItemsInWebStorageOverrides:
					args.allowUncategorizedItemsInWebStorageOverrides,
				allowUncategorizedItemsInIndexedDBOverrides:
					args.allowUncategorizedItemsInIndexedDBOverrides,
				perimeter: args.perimeter,
			},
		});

		// Ensures that the AWC is constructed before overwriting any native methods.
		const analyticsWebClientInstance = getAnalyticsClient();

		// If AWC is not initialized, it will be undefined
		const isAnalyticsInitialized: boolean = !!analyticsWebClientInstance;

		if (args.analyticsEnabled && !isAnalyticsInitialized) {
			throw new Error(
				`Failed to initialize AnalyticsWebClient. Analytics logging will not be set for native method overrides.`,
			);
		}

		if (fg('platform_bsc_sync_initialize_controls')) {
			// Install every override on the calling tick. Because nothing below awaits, this whole
			// `async` function body runs to completion synchronously — so a product that calls
			// `initializeControls` without awaiting it still has all overrides in place the moment the
			// call returns. There is no longer a window in which `document.cookie` / `setItem` /
			// `indexedDB.open` bypass consent enforcement.
			//
			// Synchronous throws still reach the `catch` below, so the `initializeControlsError`
			// operational event and the abort-on-first-failure behaviour are unchanged. That would NOT
			// hold if we merely dropped the `await`s from the legacy `create*Passthrough` functions: a
			// sync throw inside an `async` function surfaces as a rejected promise, not a thrown error.
			createDocumentCookiePassthroughSync();

			if (getBscGlobalState().webStorageEnabled) {
				createWebStoragePassthroughSync(WebStorageType.LocalStorage);
				createWebStoragePassthroughSync(WebStorageType.SessionStorage);
			}

			if (getBscGlobalState().indexedDbEnabled) {
				createIndexedDBPassthroughSync(getIndexedDbPassthroughConfig());
			}
		} else {
			await createDocumentCookiePassthrough();

			const isWebStorageControlsEnabled = getBscGlobalState().webStorageEnabled;
			if (isWebStorageControlsEnabled) {
				// Serial, not `Promise.all`: `BSCIndexedDB.boot()` short-circuits on its promise cache for
				// concurrent callers, so parallelising would install the SessionStorage override before
				// the DB is open.
				await createWebStoragePassthrough(WebStorageType.LocalStorage);
				await createWebStoragePassthrough(WebStorageType.SessionStorage);
			}

			const isIndexedDbEnabled = getBscGlobalState().indexedDbEnabled;
			if (isIndexedDbEnabled) {
				await createIndexedDBPassthrough(getIndexedDbPassthroughConfig());
			}
		}

		Logger.debug('initializeControls finished');

		const storageCleanupPromise = cleanupStorage();
		initResult = Object.assign(initResult ?? {}, { storageCleanupPromise });
	} catch (e: any) {
		Logger.errorWithOperationalEvent({
			action: 'initializeControlsError',
			message: `Failed to initialize browser storage controls. ${e.message || ''}`,
		});
	}

	return initResult;
};

export { createDocumentCookiePassthrough, createIndexedDBPassthrough, createWebStoragePassthrough };
